import React, {PropsWithChildren, useContext, useEffect, useState} from 'react';
import * as firebase from 'firebase';
import {auth, firestore} from '../firebase';
import UserContext from '../contexts/UserContext';
import {User} from "myfitworld-model";
import {acceptInvitation} from "../api/usersApi";
import {INV_KEY, useInvitationProvider} from "./InvitationProvider";
import globalState from "../globalState";
import Role from "myfitworld-model/dist/enums/Role";
import { useTranslationContext } from '../contexts/TranslationProvider';
import VerificationDialog from 'src/pages/schedule/VerificationDialog';
import {defaultUserProviderState, formatDatePropFromUser, getUserRole, UserProviderRolesInterface, withDefaultsUserProviderState } from './helpers';
import { navigate } from '@reach/router';
export interface UserProviderState {
  user: User | null;
  loading: boolean;
  needsInvitation?: boolean;
  organizationOverride?: string;
  enterApplication?: boolean;
  userRole: UserProviderRolesInterface,
  resetUser: () => void;
}

interface UserWithCurrentRole extends User {
  currentRole?: Role;
}

const extractCurrentOrganization = (organizations: { role: string, id: string }[], organizationId: string) => {
  if (organizations && organizationId) {
    return organizations.find(o => o.id === organizationId);
  }
}

export const UserProvider = ({children}: PropsWithChildren<any>) => {
  const {setLocale} = useTranslationContext();
  const [state, setState] = useState<UserProviderState>(defaultUserProviderState)


  const setOrganizationOverride = (organizationId: string | undefined) => {
    let actingAs = (state.user as any) as UserWithCurrentRole;
    if (organizationId) {
      actingAs.currentRole = Role.Admin;
      actingAs.currentOrganization = organizationId;
    } else {
      actingAs.currentRole = Role.SuperAdmin;
    }
    setState({...state, user: actingAs, organizationOverride: organizationId})
  }

  const {invitation, setInvitation} = useInvitationProvider();

  useEffect(() => {

    const update = async () => {
      const role = state.user ? (
        // @ts-ignore
        state.user.role || state.user.currentRole || // legacy fallback
        (state.user.isSuperAdmin ? Role.SuperAdmin : undefined) ||
        (state.user.organizations && state.user.currentOrganization ? extractCurrentOrganization(state.user.organizations, state.user.currentOrganization)?.role : undefined)
      ) : undefined;

      globalState.update(s => {
        s.currentRole = role;
        s.currentUserId = state.user ? state.user.id : undefined;
        s.language = state.user? state.user.language: undefined;
      });

      if (role && role !== Role.SuperAdmin && state.user && state.user.currentOrganization) {
        if (state.user.currentOrganization) {
          firestore
            .collection("organizations")
            .doc(state.user.currentOrganization)
            .onSnapshot((querySnapshot) => {
              globalState.update((s: any) => {
                s.currentOrganization = { id: querySnapshot.id, ...querySnapshot.data() };
              });
            });
        }
      }
    };
    update();
  }, [state.user]);
  
  function handleSendInvitations(newUser:User){
    if (invitation && !acceptInvitation(newUser, invitation)) {
      setState(withDefaultsUserProviderState({ needsInvitation: true }));
    } else {
      setInvitation(null);
    }
  }

  const handleAuthenticatedUser = (user: User) => {
    if (user.language && user.language !== state?.user?.language) {
      //@ts-ignore
      setLocale(user.language as Locale);
    }
  
    setState(prevState => {
      const prevUser: User = prevState.user || ({} as User);
      const updatedUser: User = { ...prevUser, ...user };
  
      const userChanged = Object.keys(user).some(
        key => updatedUser[key as keyof User] !== prevUser[key as keyof User]
      );
  
      if (!userChanged && !prevState.loading && prevState.enterApplication) {
        return prevState;
      }
  
      return {
        ...prevState,
        user: updatedUser,
        loading: false,
        enterApplication: true,
        userRole: getUserRole(updatedUser),
      };
    });
  };
  const handleInvitationUser = (userAuth: firebase.User, user: User) => {
    const existingUser = user;
    const roleExists = (invitation && invitation!==null) ? 
      existingUser.organizations?.some(org => org.id === invitation.organization) : false;
  
    if (roleExists) {
      window.localStorage.setItem('accountExists', 'accountExists');
      window.localStorage.removeItem(INV_KEY);
      setState(withDefaultsUserProviderState({ loading: false }));
      return;
    }
  
    const newUser: User = {
      id: user.id,
      ...existingUser,
      myUrl: user.id,
      email: userAuth.email || "",
      firstName: existingUser.firstName || "",
      lastName: existingUser.lastName || "",
      phoneNumber: userAuth.phoneNumber || "",
      avatarURL: userAuth.photoURL || undefined,
      createdAt: formatDatePropFromUser(userAuth.metadata.creationTime),
      lastSignInTime: formatDatePropFromUser(userAuth.metadata.lastSignInTime),
    };
  
    handleSendInvitations(newUser);
  };
  const handleNonExistentUser = (userAuth: firebase.User, snapshot: firebase.firestore.DocumentData) => {
    if (!invitation) {
      setState(withDefaultsUserProviderState({ loading: false, needsInvitation: true }));
      return;
    }
  
    const newUser: User = {
      id: userAuth.uid,
      ...snapshot.data(),
      myUrl: userAuth.uid,
      email: userAuth.email || "",
      firstName: "",
      lastName: "",
      phoneNumber: userAuth.phoneNumber || "",
      avatarURL: userAuth.photoURL || undefined,
      createdAt: formatDatePropFromUser(userAuth.metadata.creationTime),
      lastSignInTime: formatDatePropFromUser(userAuth.metadata.lastSignInTime),
    };
  
    handleSendInvitations(newUser);
  };

  const resetUser = () => {
    setState(defaultUserProviderState);
    globalState.update((s: any) => {
      s.currentUserId = undefined;
      s.currentOrganization = undefined;
      s.currentRole = undefined;
      s.language = undefined;
    });
    window.localStorage.removeItem('accountExists');
    window.localStorage.removeItem(INV_KEY);
    navigate(`/en`);
  };
  
  
  useEffect(() => {
    const subscribeToUser = (userAuth: firebase.User): (() => void) => {
      const userRef = firestore.collection('users').doc(userAuth.uid);
  
      return userRef.onSnapshot(
        snapshot => {
          if (!snapshot.exists) {
            handleNonExistentUser(userAuth, snapshot);
            return;
          }
  
          const userData = snapshot.data();
          if (!userData) return;
  
          const user = { id: snapshot.id, ...userData } as User;
  
          invitation
            ? handleInvitationUser(userAuth, user)
            : handleAuthenticatedUser(user);
        },
        error => {
          console.debug('Error getting user', error);
        }
      );
    };
  
    let unsubscribeUser: () => void = () => {};
  
    const unsubscribeAuth = auth.onAuthStateChanged(userAuth => {
      if (userAuth?.uid) {
        unsubscribeUser = subscribeToUser(userAuth);
      } else {
        setState(withDefaultsUserProviderState({ loading: false }));
      }
    });
  
    return () => {
      unsubscribeUser();
      unsubscribeAuth();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invitation]);
  

  return (
    <UserContext.Provider value={{...state, setOrganizationOverride, resetUser}}>
      <VerificationDialog />
      {children}
    </UserContext.Provider>
  );
}

export const useUserProvider = () => {
  const context = useContext(UserContext);

  if (context === undefined) {
    throw new Error(
      'useUserProvider must be used within a UserProvider'
    );
  }
  return context;
};


export default UserProvider;
